import { useEffect, useState } from 'react';
import { DEAL_PRIVILEGE, TABS } from '@constants/common.constants';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '@/state';
import { fetchListDeals, setRefreshing as setDealsRefreshing } from '@/state/slice/dealSlice';
import DealPrivilegeTabContent from './components/DealPrivilegeTabContent';
import { fetchListPrivileges, setRefreshing as setPrivilegesRefreshing } from '@/state/slice/privilegeSlice';
import { fetchMilesConversionRate } from '@/state/slice/milesConversionSlice';
import BreadCrumb from '@components/BreadCrumb';
import { QUERY_PARAMS, ROUTES } from '@constants/route.constant';
import { useLocation, useNavigate } from 'react-router-dom';
import useMaintenanceCheck from '@hooks/useMaintenanceCheck';
import PullToRefresh from '@components/PullToRefresh';

export default function DealsAndPrivileges() {
	const navigate = useNavigate();
	const location = useLocation();
	const params = new URLSearchParams(location.search);

	const tabs = [TABS.ALL, TABS.DEALS, TABS.PRIVILEGES];

	const dispatch = useDispatch<AppDispatch>();
	const user = useSelector((state: AppState) => state.userState.user);
	const dealsData = useSelector((state: AppState) => state['dealState']);
	const privilegeData = useSelector((state: AppState) => state['privilegeState']);
	const [selectedTab, setSelectedTab] = useState((params.get(QUERY_PARAMS.TAB) as TABS) ?? TABS.ALL);
	useMaintenanceCheck();

	const getMilesConversionRate = async () => {
		try {
			dispatch(fetchMilesConversionRate(user.merchant?.acceptedCurrencyCode));
		} catch (error) {
			console.error('Error fetching deals:', error);
		}
	};

	const onRefresh = async (): Promise<void> => {
		dispatch(setDealsRefreshing(true));
		dispatch(setPrivilegesRefreshing(true));
		if (selectedTab === TABS.DEALS) {
			await getDeals();
		} else if (selectedTab === TABS.PRIVILEGES) {
			await getPrivileges();
		} else {
			await Promise.all([getDeals(), getPrivileges()]);
		}
	};

	const getDeals = async () => {
		try {
			dispatch(fetchListDeals(user.merchant._id));
		} catch (error) {
			console.error('Error fetching deals:', error);
		}
	};

	const getPrivileges = async () => {
		try {
			dispatch(fetchListPrivileges(user.merchant._id));
		} catch (error) {
			console.error('Error fetching privileges:', error);
		}
	};

	useEffect(() => {
		if (user._id !== '') {
			void getMilesConversionRate();
			void getDeals();
			void getPrivileges();
		}
	}, [user?._id]);

	const onChangeTab = (tab: string) => {
		setSelectedTab(tab as TABS);
		navigate(`/${ROUTES.DEALS_PRIVILEGES}?${QUERY_PARAMS.TAB}=${tab}`, { replace: true });
	};

	const shouldShowDealsSkeleton = dealsData.isLoading && dealsData.deals.length === 0 && !dealsData.isRefreshing;
	const shouldShowPrivilegesSkeleton = privilegeData.isLoading && privilegeData.privileges.length === 0 && !privilegeData.isRefreshing;

	const sectionConfig = [
		{
			tabType: TABS.DEALS,
			tabName: 'Deals',
			description: "These are pre-paid by customer. Apply voucher to customer's bill and collect any remaining balance, if applicable.",
			data: dealsData.deals,
			isLoading: shouldShowDealsSkeleton,
			type: DEAL_PRIVILEGE.DEAL,
		},
		{
			tabType: TABS.PRIVILEGES,
			tabName: 'Privileges',
			description:
				'These do not need to be pre-purchased. Simply apply the privilege as per the terms & conditions and collect any payment, if applicable.',
			data: privilegeData.privileges,
			isLoading: shouldShowPrivilegesSkeleton,
			type: DEAL_PRIVILEGE.PRIVILEGE,
		},
	];

	return (
		<div>
			<div className='flex flex-col items-center'>
				<BreadCrumb title='Deals & Privileges' />
				<div className={`flex gap-6 mt-6 desktop:mb-14`}>
					{tabs.map((tab) => (
						<div
							className={`uppercase cursor-pointer pb-2 font-semibold leading-[17.05px] ${
								selectedTab === tab ? 'text-white border-b-[5px] border-b-cyan' : 'text-blue-grey-light'
							}`}
							onClick={() => onChangeTab(tab)}
							key={tab}>
							{tab}
						</div>
					))}
				</div>
			</div>

			<PullToRefresh key={selectedTab} onRefresh={onRefresh} childHeight={140}>
				<DealPrivilegeTabContent selectedTab={selectedTab} sectionConfig={sectionConfig} />
			</PullToRefresh>
		</div>
	);
}
